import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { catchError, of } from 'rxjs';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { ReturnFrequenciesService } from '../../../domain/services/admin/return-frequencies.service';
import { DialogService } from '../../../domain/services/dialog/dialog.service';

export interface ReturnFrequency {
  id?: number;
  name?: string;
  frequencyName?: string;
  code: string;
  filingPeriod: string;
  active?: boolean;
  isActive?: boolean;
}

@Component({
  selector: 'app-return-frequency-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule,
    MatSelectModule, MatButtonModule, MatIconModule, MatDialogModule],
  template: `
    <div style="padding:8px;">
      <h2 mat-dialog-title style="display:flex;align-items:center;gap:12px;font-size:20px;font-weight:600;color:#1e293b;">
        <mat-icon style="color:#2563eb;">calendar_month</mat-icon>
        {{ data.freq ? 'Edit Frequency' : 'Add Frequency' }}
      </h2>
      <mat-dialog-content>
        <form [formGroup]="form" style="display:flex;flex-direction:column;gap:16px;min-width:420px;">
          <div class="field-group">
            <label style="font-size:13px;font-weight:500;color:#475569;margin-bottom:6px;display:block;">Name <span style="color:#dc2626;">*</span></label>
            <mat-form-field appearance="outline" subscriptSizing="dynamic" style="width:100%;">
              <input matInput formControlName="name" placeholder="e.g. Monthly">
              <mat-error>Name is required</mat-error>
            </mat-form-field>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
            <div class="field-group">
              <label style="font-size:13px;font-weight:500;color:#475569;margin-bottom:6px;display:block;">Code <span style="color:#dc2626;">*</span></label>
              <mat-form-field appearance="outline" subscriptSizing="dynamic" style="width:100%;">
                <input matInput formControlName="code" placeholder="e.g. M">
                <mat-error>Code is required</mat-error>
              </mat-form-field>
            </div>
            <div class="field-group">
              <label style="font-size:13px;font-weight:500;color:#475569;margin-bottom:6px;display:block;">Status</label>
              <mat-form-field appearance="outline" subscriptSizing="dynamic" style="width:100%;">
                <mat-select formControlName="isActive">
                  <mat-option [value]="true">Active</mat-option>
                  <mat-option [value]="false">Inactive</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>
          <div class="field-group">
            <label style="font-size:13px;font-weight:500;color:#475569;margin-bottom:6px;display:block;">Filing Period</label>
            <mat-form-field appearance="outline" subscriptSizing="dynamic" style="width:100%;">
              <mat-select formControlName="filingPeriod" placeholder="Select filing period">
                <mat-option value="Daily">Daily</mat-option>
                <mat-option value="Weekly">Weekly</mat-option>
                <mat-option value="Bi-Weekly">Bi-Weekly</mat-option>
                <mat-option value="Monthly">Monthly</mat-option>
                <mat-option value="Bi-Monthly">Bi-Monthly</mat-option>
                <mat-option value="Quarterly">Quarterly</mat-option>
                <mat-option value="Semi-Annually">Semi-Annually</mat-option>
                <mat-option value="Annually">Annually</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </form>
      </mat-dialog-content>
      <mat-dialog-actions align="end" style="padding:16px 0 0;gap:12px;">
        <button mat-stroked-button (click)="dialogRef.close()" style="color:#2563eb;"><mat-icon>arrow_back</mat-icon> GO BACK</button>
        <button mat-flat-button color="primary" [disabled]="form.invalid" (click)="save()"><mat-icon>save</mat-icon> SAVE</button>
      </mat-dialog-actions>
    </div>
  `
})
export class ReturnFrequencyDialogComponent {
  form: FormGroup;
  constructor(
    public dialogRef: MatDialogRef<ReturnFrequencyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { freq?: ReturnFrequency }
  ) {
    this.form = new FormGroup({
      name: new FormControl(data.freq?.name ?? data.freq?.frequencyName ?? null, [Validators.required]),
      code: new FormControl(data.freq?.code ?? null, [Validators.required]),
      filingPeriod: new FormControl(data.freq?.filingPeriod ?? null),
      isActive: new FormControl(data.freq?.isActive ?? data.freq?.active ?? true),
    });
  }
  save() { this.dialogRef.close(this.form.value); }
}

@Component({
  selector: 'app-return-frequencies',
  standalone: true,
  imports: [
    CommonModule, MatTableModule, MatButtonModule, MatIconModule,
    MatCardModule, MatPaginatorModule, MatSortModule,
  ],
  templateUrl: './return-frequencies.component.html',
  styleUrl: './return-frequencies.component.css',
})
export class ReturnFrequenciesComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<ReturnFrequency>;

  displayedColumns = ['frequencyName', 'code', 'filingPeriod', 'active', 'actions'];
  dataSource = new MatTableDataSource<ReturnFrequency>([]);

  constructor(
    private readonly breadcrumbService: BreadcrumbService,
    private readonly returnFrequenciesService: ReturnFrequenciesService,
    private readonly dialog: MatDialog,
    private readonly dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'account_balance', url: '/admin/return-frequencies', label: 'Administrative' },
      { icon: 'calendar_month', url: '/admin/return-frequencies', label: 'Return Frequencies' },
    ]);
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadData(): void {
    this.returnFrequenciesService.getAll()
      .pipe(catchError(() => of([])))
      .subscribe((data) => {
        this.dataSource.data = data.map((d: any) => ({
          ...d,
          frequencyName: d.name || d.frequencyName,
          active: d.isActive ?? d.active ?? true,
        }));
      });
  }

  addFrequency(): void {
    const dialogRef = this.dialog.open(ReturnFrequencyDialogComponent, {
      width: '520px', data: {}
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.returnFrequenciesService.create(result).subscribe(() => this.loadData());
      }
    });
  }

  editFrequency(freq: ReturnFrequency): void {
    const dialogRef = this.dialog.open(ReturnFrequencyDialogComponent, {
      width: '520px', data: { freq }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result && freq.id) {
        this.returnFrequenciesService.update(freq.id, result).subscribe(() => this.loadData());
      }
    });
  }

  deleteFrequency(freq: ReturnFrequency): void {
    if (!freq.id) return;
    this.dialogService.openDeletedDialog(
      'Delete Return Frequency',
      `Are you sure you want to delete "${freq.name || freq.frequencyName}"? This action cannot be undone.`,
      (confirmed: boolean) => {
        if (confirmed) {
          this.dataSource.data = this.dataSource.data.filter(d => d.id !== freq.id);
        }
      }
    );
  }
}
